import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getProfile } from "@/lib/api/users";
import { useAuth } from "@/hooks/use-auth";
import { fetchCvData } from "@/lib/api/cv";
import { User, ShieldCheck, FileText, Lock, LayoutGrid } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Extracted Profile Components
import { ProfileHeader } from "@/components/profile/profile-header";
import { PrivacySettingsPanel } from "@/components/profile/privacy-settings-panel";
import { PersonalInfoForm } from "@/components/profile/personal-info-form";
import { ProfileProgressCard } from "@/components/profile/profile-progress-card";
import { CvSection } from "@/components/profile/cv-section";
import { AcademicStatusCard } from "@/components/profile/academic-status-card";
import { ChangePasswordCard } from "@/components/profile/change-password-card";
import { FormSkeleton } from "@/components/loading";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { user, hasPermission } = useAuth();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: async () => {
      return getProfile(user!.id);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch saved CV data
  const { data: savedCvData, isLoading: isLoadingCvData } = useQuery({
    queryKey: ["my-cv-data", user?.id],
    queryFn: () => fetchCvData(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading)
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <FormSkeleton fields={6} />
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <ProfileHeader user={user} profile={profile ?? null} />

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 flex-wrap h-auto">
          <TabsTrigger value="overview" className="gap-2">
            <LayoutGrid className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="personal" className="gap-2">
            <User className="h-4 w-4" /> Personal Info
          </TabsTrigger>
          <TabsTrigger value="cv" className="gap-2">
            <FileText className="h-4 w-4" /> CV &amp; Portfolio
          </TabsTrigger>
          <TabsTrigger value="privacy" className="gap-2">
            <ShieldCheck className="h-4 w-4" /> Privacy
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Lock className="h-4 w-4" /> Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="md:col-span-1">
              <ProfileProgressCard
                profile={profile ?? null}
                isAdmin={hasPermission("manage:users")}
              />
            </div>
            <div className="md:col-span-2">
              <AcademicStatusCard user={user} profile={profile ?? null} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="personal" className="space-y-6">
          <PersonalInfoForm user={user} profile={profile ?? null} />
        </TabsContent>

        <TabsContent value="cv" className="space-y-6">
          <CvSection
            user={user}
            profile={profile ?? null}
            savedCvData={savedCvData}
            isLoadingData={isLoadingCvData}
          />
        </TabsContent>

        <TabsContent value="privacy" className="space-y-6">
          <PrivacySettingsPanel user={user} profile={profile ?? null} />
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <ChangePasswordCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
